
import { useState } from 'react';

const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    duration: '',
    message: ''
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'message') {
      setCharCount(value.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d487i1fjsic9gsa4uqvg', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          duration: '',
          message: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-stone-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-stone-800 mb-6">Időpontfoglalás</h2>
            <p className="text-xl text-stone-600 leading-relaxed">
              A visszajelzés fontos. Írj nekem üzenetet, és válaszolok.
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                Köszönöm az üzenetet! Hamarosan válaszolok.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                Hiba történt az üzenet küldése során. Kérjük, próbálja újra.
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-stone-700 font-medium mb-2">
                    Név *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder="Az Ön neve"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-stone-700 font-medium mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    placeholder="+36 XX XXX XXXX"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-stone-700 font-medium mb-2">
                  Email cím
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  placeholder="email@example.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="service" className="block text-stone-700 font-medium mb-2">
                    Szolgáltatás
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Válasszon szolgáltatást</option>
                    <option value="teljes-test-masszazs">Teljes test masszázs</option>
                    <option value="nyak-vall-masszazs">Nyak és váll masszázs</option>
                    <option value="fej-arc-masszazs">Fej és arc masszázs</option>
                    <option value="talp-lab-masszazs">Talp és láb masszázs</option>
                    <option value="derek-hat-masszazs">Derék és hát masszázs</option>
                    <option value="derek-hat-vall-nyak-masszazs">Derék, hát, váll és nyak masszázs</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="duration" className="block text-stone-700 font-medium mb-2">
                    Időtartam
                  </label>
                  <select
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Válasszon időtartamot</option>
                    <option value="30">30 perc - 4.000 Ft</option>
                    <option value="60">60 perc - 8.000 Ft</option>
                    <option value="90">90 perc - 12.000 Ft</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-stone-700 font-medium mb-2">
                  Üzenet
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                  placeholder="Írja le kérését, kérdését vagy egyéb megjegyzését..."
                />
                <div className="text-right text-sm text-stone-500 mt-1">
                  {charCount}/500 karakter
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-stone-400 text-white py-4 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line mr-2"></i>
                {isSubmitting ? 'Küldés...' : 'Kapcsolatfelvétel'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-stone-600 mb-4">Vagy foglaljon időpontot közvetlenül:</p>
              <a
                href="https://app.minup.io/book/zoltan-grendorf/category/67f7bc13-1ad6-40f0-addc-782133eed473"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-line mr-2"></i>
                Azonnali időpontfoglalás
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
